import { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { GroupType, UpdateChargeCodeRequest } from 'graphql-query-contracts';
import { updateChargeCode as updateChargeCodeMutation } from 'graphql-queries';

export class UpdateChargeCodeError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'UpdateChargeCodeError';
  }
}

export type UpdateChargeCodeInput = {
  chargeCodeId: string;
  chargeCode: string;
  glCode: string;
  description?: string;
  channelId: string;
};

export const useUpdateChargeCode = () => {
  const [mutateUpdateChargeCode, { loading, error }] = useMutation(
    updateChargeCodeMutation
  );
  const [thrownError, setThrownError] = useState<
    UpdateChargeCodeError | undefined
  >();

  const updateChargeCode = async ({
    chargeCodeId,
    chargeCode,
    glCode,
    description,
    channelId,
  }: UpdateChargeCodeInput): Promise<boolean> => {
    // Clear any previous thrown error
    setThrownError(undefined);

    const updateChargeCodeRequest: UpdateChargeCodeRequest = {
      id: chargeCodeId,
      chargeCode,
      glCode,
      description,
      groupId: channelId,
      groupType: GroupType.GroupTypeActivateChannel,
    };

    try {
      const result = await mutateUpdateChargeCode({
        variables: {
          updateChargeCodeRequest,
        },
      });

      return !!result.data?.updateChargeCode?.chargeCode?.id;
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to update charge code';

      const updateChargeCodeError = new UpdateChargeCodeError(message, {
        cause: err,
      });

      setThrownError(updateChargeCodeError);

      throw updateChargeCodeError;
    }
  };

  const updateChargeCodeError = useMemo(() => {
    // Prioritize thrown error over mutation hook error
    if (thrownError) return thrownError;

    if (!error) return undefined;

    const message =
      error instanceof Error && error.message
        ? error.message
        : 'Failed to update charge code';

    return new UpdateChargeCodeError(message, { cause: error });
  }, [thrownError, error]);

  return {
    updateChargeCode,
    loading,
    error: updateChargeCodeError,
  };
};
